import { FormikProps } from 'formik'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { AccountTypeModal } from '@pages/Traders-Room/Trading-Accounts/NewTradingAccount/modals/AccountType.modal'
import { TradingPlatformModal } from '@pages/Traders-Room/Trading-Accounts/NewTradingAccount/modals/TradingPlatform.modal'
import { LeverageModal } from '@pages/Traders-Room/Trading-Accounts/modals/Leverage.modal'

import { useAccountInfo } from '@context/AccountInfoContext/AccountInfoContext.Provider'
import { useModal } from '@context/ModalsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import { LeverageTypeDto, isLeverageTypeDynamic } from '@model/LeverageTypeDto'
import { NameDto } from '@model/NameDto'
import { PlatformTypeEnum } from '@model/PlatformTypeEnum'

import { RadioModal } from '@shared/modals/Radio.modal'

import { CreateDemoTradingAccountFormValues, OuterProps } from './CreateDemoTradingAccount.types'

const DEFAULT_VIRTUAL_DEPOSIT = 100000

export const useModals = (props: FormikProps<CreateDemoTradingAccountFormValues> & OuterProps) => {
  const apiClient = useApiClient(ClientApiClient)
  const { values, setFieldValue, currencies } = props
  const { t } = useTranslation()
  const location = useLocation()
  const { accountInfo } = useAccountInfo()
  const isMobile = useWindowResize()

  const [accountTypes, setAccountTypes] = useState<NameDto[]>([])
  const [classificationTypes, setClassificationTypes] = useState<NameDto[]>([])

  const { selectedPlatformType } = location.state || {}
  const [tmpPlatformType, setTmpPlatformType] = useState<NameDto | undefined>(
    values.platformType || selectedPlatformType
  )

  const handleSetTradingPlatform = async (platform?: NameDto) => {
    const currentPlatformType = tmpPlatformType || platform
    setFieldValue('platformType', currentPlatformType)
    if (currentPlatformType) props.onChangePlatform(currentPlatformType.id)
    if (
      currentPlatformType &&
      (currentPlatformType.id === PlatformTypeEnum.TickmillTrader ||
        currentPlatformType.id === PlatformTypeEnum.TradingView)
    ) {
      const dynamicLeverage = (await apiClient.getLeverageTypes(platform?.id)).find((l) =>
        isLeverageTypeDynamic(l.id)
      )
      setFieldValue('virtualDeposit', DEFAULT_VIRTUAL_DEPOSIT)
      if (dynamicLeverage) setFieldValue('leverageType', dynamicLeverage)
    } else {
      setFieldValue('leverageType', undefined)
      setFieldValue('virtualDeposit', 0)
    }
    setFieldValue('accountType', undefined)
  }

  const handleSetLeverage = (leverage: LeverageTypeDto) => {
    setFieldValue('leverageType', leverage)
  }

  const handleSetAccountType = (accountType: NameDto) => {
    setFieldValue('accountType', accountType)
  }

  const handleSetClassificationType = (classificationTypeId: string) => {
    const classificationType = classificationTypes.find(
      (x) => x.id.toString() === classificationTypeId.toString()
    )
    setFieldValue('classificationType', classificationType)
  }

  const handleSetAccountCurrency = (currency: string) => {
    setFieldValue(
      'accountCurrency',
      currencies.find((curr) => curr.id === currency)
    )
    setFieldValue('accountType', undefined)
    setFieldValue('leverageType', undefined)
  }

  const handleSetTmpPlatformType = (platform: NameDto) => setTmpPlatformType(platform)

  const accountTypeModal = useModal(AccountTypeModal, {
    initialValue: isMobile ? values.accountType?.id.toString() : undefined,
    accountTypes: accountTypes,
    isDemo: false,
    onSelect: handleSetAccountType,
  })

  const currencyModal = useModal(RadioModal, {
    title: t('Campaigns.Currency'),
    onSelect: handleSetAccountCurrency,
    initialValue: isMobile ? values.accountCurrency?.id : undefined,
    simpleOptions: currencies.map((curr) => curr.id),
  })

  const classificationTypeModal = useModal(RadioModal, {
    title: t('Trading Account.Classification Type'),
    onSelect: handleSetClassificationType,
    initialValue: isMobile ? values.classificationType?.id.toString() : undefined,
    simpleOptions: classificationTypes,
  })

  const tradingPlatformModal = useModal(TradingPlatformModal, {
    tradingAccountsAccountInfo: accountInfo,
    demo: true,
    initialValue: values.platformType?.id.toString(),
    onConfirm: handleSetTradingPlatform,
  })

  const leverageModal = useModal(LeverageModal, {
    platformTypeId: values.platformType?.id,
    currentLeverageId: values.leverageType?.id,
    restrictions: [],
    onSelect: handleSetLeverage,
    title: t('Trading Account.Select Leverage'),
  })

  return {
    classificationTypes,
    accountTypes,
    setAccountTypes,
    setClassificationTypes,
    tmpPlatformType,
    handleSetTradingPlatform,
    handleSetLeverage,
    handleSetAccountType,
    handleSetClassificationType,
    handleSetAccountCurrency,
    handleSetTmpPlatformType,
    leverageModal,
    tradingPlatformModal,
    accountTypeModal,
    currencyModal,
    classificationTypeModal,
    accountInfo,
    currencies,
  }
}
